import { html, View } from 'rune-ts';
import { FundingModalController } from '@services/MarppleShop/renderApp/components/cells/funding/FundingModalController.ts';
import klass from './FundingModal.module.scss';
import { pipe, map, toArray, filter, flat, zipWithIndex, slice } from '@fxts/core';
import { font } from '@services/MarppleShop/shared/typography';
import {
  FundingSupporterItem,
  FundingTopRankSupporterItem,
} from '@services/MarppleShop/renderApp/components/cells/funding/FundingSection.ts';
import { doIf } from '@services/MarppleShop/shared/util';

interface FundingModalBodyProps {
  fundingModalController: FundingModalController;
}

export class FundingModalRanking extends View<FundingModalBodyProps> {
  fundingModalController: FundingModalController;

  constructor(props: FundingModalBodyProps) {
    super(props);
    this.fundingModalController = props.fundingModalController;
  }

  protected override template() {
    const top_3_rankers = pipe(this.fundingModalController.getProps().supporters.data, slice(0, 3), toArray);

    return html`
      <div class="${klass.ranker_tab} ${this.fundingModalController.ranking_container}">
        <!-- 1 ~ 3위에 랭크한 후원자들 -->
        ${doIf(
          () => html`
            <section class="${klass.ranker_funding_items}">
              ${pipe(
                top_3_rankers,
                zipWithIndex,
                map(
                  ([rank, funding_support_ranker]) =>
                    new FundingTopRankSupporterItem({ ...funding_support_ranker, rank: rank + 1 }),
                ),
                toArray,
              )}
              ${doIf(() => html`<div class="${klass.ranker_blank_space}"></div>`, top_3_rankers.length === 2)}
            </section>
          `,
          !!this.fundingModalController.getProps().supporters.data.length,
        )}

        <!-- 4위 이상 랭크한 나머지 후원자들 -->
        <!-- SSR로 초기 렌더링 -->
        ${pipe(
          this.fundingModalController.getProps().supporters.data,
          slice(3),
          zipWithIndex,
          map(([rank, funding]) => new FundingSupporterItem({ ...funding, rank: rank + 4 })),
          toArray,
        )}

        <!-- CSR로 무한스크롤매니저가 렌더해주는 아이템들 -->
        ${pipe(
          this.fundingModalController.rankingInfiniteScrollManager?.getItems() || [],
          flat,
          zipWithIndex,
          map(
            ([rank, funding]) =>
              new FundingSupporterItem({
                ...funding,
                rank: this.fundingModalController.getProps().supporters.data.length + rank + 1,
              }),
          ),
          toArray,
        )}
      </div>
    `;
  }

  protected override onRender() {
    this.fundingModalController.createRankingInfiniteScrollManager();
  }

  protected override onMount() {
    if (this.fundingModalController.rankingInfiniteScrollManager)
      this.fundingModalController.rankingInfiniteScrollManager?.off();

    this.fundingModalController.rankingInfiniteScrollManager?.on();
  }

  protected override onUnmount() {
    this.fundingModalController.rankingInfiniteScrollManager?.off();
  }
}

export class FundingModalCreatedAt extends View<FundingModalBodyProps> {
  fundingModalController: FundingModalController;

  constructor(props: FundingModalBodyProps) {
    super(props);
    this.fundingModalController = props.fundingModalController;
  }

  protected override template() {
    // 최신순은 Only CSR로 무한스크롤매니저가 렌더링
    return html`
      <ul class="${klass.funding_items} ${this.fundingModalController.created_at_container}">
        ${pipe(
          this.fundingModalController.createdAtInfiniteScrollManager?.getItems() || [],
          flat,
          map((funding) => new FundingSupporterItem(funding)),
          toArray,
        )}
      </ul>
    `;
  }

  protected override onRender() {
    this.fundingModalController.createCreatedAtInfiniteScrollManager();
  }

  protected override onMount() {
    if (this.fundingModalController.createdAtInfiniteScrollManager)
      this.fundingModalController.createdAtInfiniteScrollManager?.off();

    this.fundingModalController.createdAtInfiniteScrollManager?.on();
  }

  protected override onUnmount() {
    this.fundingModalController.createdAtInfiniteScrollManager?.off();
  }
}

export class FundingModalTabs extends View<FundingModalBodyProps> {
  fundingModalController: FundingModalController;

  constructor(props: FundingModalBodyProps) {
    super(props);
    this.fundingModalController = props.fundingModalController;
  }

  protected override template() {
    const selected_tab = this.fundingModalController.getSelectedTab();
    return html`
      <nav class="${klass.tabs}">
        ${pipe(
          this.fundingModalController.tabs,
          map(
            (tab) => html`
              <li data-key="${tab.key}" ${selected_tab === tab.key ? html`data-is-selected` : ''}>
                <span class="${font('14_medium')}">${tab.name}</span>
              </li>
            `,
          ),
          toArray,
        )}
      </nav>
    `;
  }

  protected override onRender() {
    this.addEventListener('click', (event) => {
      const $element = event.target as HTMLElement;
      const $selected = $element.closest('li') as HTMLElement;
      const key = String($selected?.dataset?.key);

      if ($selected && key && key !== this.fundingModalController.getSelectedTab()) {
        this.fundingModalController.setSelectedTab(key);
        this.dispatchEvent(FundingModalController.ChangeFundingModalTabEvent, { bubbles: true });
        this.redraw();
      }
    });
  }
}

export class FundingModalBody extends View<FundingModalBodyProps> {
  fundingModalController: FundingModalController;
  fundingModalTabsView: FundingModalTabs;
  fundingModalRankingView: FundingModalRanking;
  fundingModalCreatedAtView: FundingModalCreatedAt;

  constructor(props: FundingModalBodyProps) {
    super(props);

    this.fundingModalController = props.fundingModalController;
    this.fundingModalRankingView = new FundingModalRanking(this.data);
    this.fundingModalCreatedAtView = new FundingModalCreatedAt(this.data);
    this.fundingModalTabsView = new FundingModalTabs(this.data);
  }

  protected override template() {
    return html`
      <div class="${klass.modal}">
        ${this.fundingModalTabsView}
        <div class="${klass.body}">
          ${pipe(
            [this.fundingModalRankingView, this.fundingModalCreatedAtView],
            filter((view) => view.toString() === this.fundingModalController.getSelectedTab()),
            toArray,
          )}
        </div>
      </div>
    `;
  }

  protected override onRender() {
    this.delegate(FundingModalController.ChangeFundingModalTabEvent, FundingModalTabs, () => this.redraw());
  }
}
